import { useState } from "react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarIcon, Loader2, CheckCircle2, Phone } from "lucide-react";
import { PageHero, SectionLabel } from "../components/site/Bits";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";
import { SERVICE_OPTIONS, BUSINESS } from "../data/content";

const TIME_SLOTS = [
  "Morning (8am – 12pm)",
  "Afternoon (12pm – 4pm)",
  "Evening (4pm – 6pm)",
  "Flexible",
];

const empty = {
  name: "", telephone: "", email: "", address: "",
  service: "", preferred_time: "", special_instructions: "",
};

export default function BookOnline() {
  const [form, setForm] = useState(empty);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.telephone || !form.email || !form.address || !form.service || !date || !form.preferred_time) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", {
        ...form,
        preferred_date: format(date, "yyyy-MM-dd"),
      });
      setDone(true);
      toast.success("Booking request sent! We'll be in touch shortly.");
      setForm(empty);
      setDate(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="book-page">
      <PageHero
        crumb="Book Online"
        label="Book Online"
        title="Book your clean in under a minute."
        subtitle="Fill in the form below and we'll confirm your booking as soon as possible. New customers receive 10% off."
      />

      <section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid gap-12 lg:grid-cols-12">
          {/* Form */}
          <div className="lg:col-span-8">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="booking-success"
                className="rounded-3xl bg-mist border border-black/5 p-12 text-center"
              >
                <CheckCircle2 className="w-14 h-14 text-brandgreen mx-auto" />
                <h2 className="mt-6 font-heading font-extrabold text-navy text-3xl tracking-tighter">
                  Booking request received!
                </h2>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                  Thank you — we've got your details and will confirm your appointment shortly.
                </p>
                <Button
                  onClick={() => setDone(false)}
                  data-testid="new-booking-btn"
                  className="mt-8 rounded-full bg-navy hover:bg-brandgreen text-white px-8"
                >
                  Make another booking
                </Button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="booking-form" className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Name" required>
                    <Input data-testid="booking-name" value={form.name} onChange={set("name")} placeholder="Your full name" className="h-12" />
                  </Field>
                  <Field label="Telephone" required>
                    <Input data-testid="booking-telephone" value={form.telephone} onChange={set("telephone")} placeholder="07..." className="h-12" />
                  </Field>
                  <Field label="Email" required>
                    <Input data-testid="booking-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="h-12" />
                  </Field>
                  <Field label="Address" required>
                    <Input data-testid="booking-address" value={form.address} onChange={set("address")} placeholder="Street, town, postcode" className="h-12" />
                  </Field>
                </div>

                <Field label="Service Required" required>
                  <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                    <SelectTrigger data-testid="booking-service" className="h-12">
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Preferred Date" required>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid="booking-date-trigger"
                          className="flex items-center justify-between w-full h-12 rounded-md border border-input bg-white px-3 text-sm text-left hover:border-brandgreen transition-colors"
                        >
                          <span className={date ? "text-navy" : "text-muted-foreground"}>
                            {date ? format(date, "PPP") : "Pick a date"}
                          </span>
                          <CalendarIcon className="w-4 h-4 text-brandgreen" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </Field>
                  <Field label="Preferred Time" required>
                    <Select value={form.preferred_time} onValueChange={(v) => setForm((f) => ({ ...f, preferred_time: v }))}>
                      <SelectTrigger data-testid="booking-time" className="h-12">
                        <SelectValue placeholder="Choose a time" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <Field label="Special Instructions">
                  <Textarea
                    data-testid="booking-instructions"
                    value={form.special_instructions}
                    onChange={set("special_instructions")}
                    placeholder="Anything we should know? Parking, pets, access, specific requests..."
                    rows={4}
                  />
                </Field>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="booking-submit"
                  className="w-full sm:w-auto rounded-full bg-brandgreen hover:bg-navy text-white h-14 px-10 text-base font-semibold"
                >
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : "Submit Booking"}
                </Button>
              </form>
            )}
          </div>

          {/* Aside */}
          <aside className="lg:col-span-4">
            <div className="sticky top-28 rounded-3xl bg-navy text-white p-8">
              <SectionLabel dark>Need help?</SectionLabel>
              <h3 className="mt-4 font-heading font-extrabold text-2xl tracking-tight">
                Prefer to book by phone?
              </h3>
              <p className="mt-3 text-white/70 text-sm leading-relaxed">
                Give us a call and we'll happily arrange everything for you.
              </p>
              <a
                href={BUSINESS.phoneHref}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-brandgreen px-6 py-3.5 font-semibold hover:bg-white hover:text-navy transition-colors duration-300"
              >
                <Phone className="w-4 h-4" /> {BUSINESS.phone}
              </a>
              <div className="mt-8 pt-6 border-t border-white/10 text-sm text-white/70 space-y-2">
                <p>Fully insured &amp; DBS checked</p>
                <p>10% off your first booking</p>
                <p>Serving Preston &amp; Lancashire</p>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-navy">
        {label} {required && <span className="text-brandgreen">*</span>}
      </Label>
      {children}
    </div>
  );
}
